import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft,
  CaretRight,
  Wrench,
  Stack,
  User,
  Warning,
  BellRinging,
  Check,
  Phone,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { JOB_STAGES } from "@/components/StageBadge";
import { isStalled, daysInStage } from "@/components/StageAge";
import { OverduePOs } from "@/components/OverduePOs";
import { UpcomingViewings } from "@/components/UpcomingViewings";
import { DesignerSends } from "@/components/DesignerSends";
import { ProjectBlockersFeed } from "@/components/ProjectBlockersFeed";
import { AlertsWidget } from "@/components/AlertsBell";
import { api } from "@/lib/api";

const KIND_STYLE = {
  Template: "border-purple-500/30 text-purple-300",
  Fabrication: "border-yellow-500/30 text-yellow-300",
  Install: "border-blue-500/30 text-blue-300",
  Service: "border-red-500/30 text-red-300",
};
// Stage colors as explicit hex so the pipeline bar always matches its dot,
// regardless of the CRM theme's Tailwind class remaps.
const STAGE_HEX = {
  Quote: "#b8b8d2",
  Template: "#c084fc",
  Fabrication: "#f05663",
  "Polish & QC": "#fb923c",
  Install: "#60a5fa",
  Complete: "#34d399",
};
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => d.toISOString().slice(0, 10);

const mondayOf = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(12, 0, 0, 0);
  return d;
};

export default function Home() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [calls, setCalls] = useState([]);
  const [holds, setHolds] = useState(null);
  const [remnants, setRemnants] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [attention, setAttention] = useState({ projects: [], conflicts: [], loading: true });
  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  const load = useCallback(async () => {
    const months = [...new Set(days.map((d) => iso(d).slice(0, 7)))];
    const safe = (p) => p.catch(() => ({ data: null }));
    const [sched, jobList, callList, holdStats, remStats, summaryRes] =
      await Promise.all([
        Promise.all(
          months.map((m) =>
            safe(api.get("/jobs/schedule", { params: { month: m } })),
          ),
        ),
        safe(api.get("/jobs")),
        safe(api.get("/service-calls")),
        safe(api.get("/reservations/stats")),
        safe(api.get("/remnants/stats")),
        safe(api.get("/dashboard/summary")),
      ]);
    safe(api.get("/follow-ups")).then((r) => setFollowUps(r.data?.items || []));
    Promise.all([
      safe(api.get("/projects/attention")),
      safe(api.get("/schedule/crew-conflicts")),
    ]).then(([a, c]) =>
      setAttention({
        projects: a.data?.projects || [],
        conflicts: c.data?.conflicts || [],
        loading: false,
      }),
    );
    setEvents(sched.flatMap((r) => r.data?.events || []));
    setJobs(jobList.data || []);
    setCalls(callList.data || []);
    setHolds(holdStats.data);
    setRemnants(remStats.data);
    setSummary(summaryRes.data);
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const weekDates = days.map(iso);
  const weekEvents = events.filter((e) => weekDates.includes(e.date));
  const stalled = jobs
    .filter(isStalled)
    .sort((a, b) => daysInStage(b) - daysInStage(a));
  const openCalls = calls.filter((c) => c.status !== "Done");
  const today = iso(new Date());

  const clearFollowUp = async (f) => {
    if (f.entity === "job") await api.patch(`/jobs/${f.id}/follow-up-done`);
    else
      await api.patch(
        `/customers/${f.id}/activities/${f.activity_id}/follow-up`,
        {
          follow_up_done: true,
        },
      );
    setFollowUps((list) => list.filter((x) => x !== f));
  };

  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Dashboard" />
      <main className="full-bleed relative z-10 w-full px-6 pb-20 pt-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Shop Overview
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              This Week
            </h1>
            <p className="mt-1 text-sm text-zinc-500" data-testid="week-range">
              {weekDates[0]} → {weekDates[6]} · {weekEvents.length} scheduled
              stops
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="week-prev"
              variant="outline"
              size="icon"
              onClick={() => shift(-1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretLeft size={18} weight="bold" />
            </Button>
            <Button
              data-testid="week-today"
              variant="outline"
              onClick={() => setWeekStart(mondayOf(new Date()))}
              className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
            >
              This week
            </Button>
            <Button
              data-testid="week-next"
              variant="outline"
              size="icon"
              onClick={() => shift(1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretRight size={18} weight="bold" />
            </Button>
          </div>
        </div>

        <div
          className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5"
          data-testid="dashboard-stats"
        >
          {[
            [
              "Active Jobs",
              jobs.filter((j) => j.stage !== "Complete").length,
              "text-white",
              "/projects",
              "bg-zinc-500",
            ],
            ["Stalled", stalled.length, "text-red-400", "/projects", "bg-red-400"],
            [
              "Open Service",
              openCalls.length,
              "text-orange-300",
              "/projects/service",
              "bg-orange-400",
            ],
            [
              "Material Held",
              holds ? holds.total : "—",
              "text-blue-300",
              "/inventory/material-on-hold",
              "bg-blue-400",
            ],
            [
              "Remnant Ft²",
              remnants ? remnants.usable_sqft : "—",
              "text-yellow-400",
              "/inventory/remnants",
              "bg-yellow-400",
            ],
            [
              "Won MTD",
              summary
                ? `$${Math.round(summary.won_this_month).toLocaleString()}`
                : "—",
              "text-emerald-300",
              "/quotes",
              "bg-emerald-400",
            ],
            [
              "Open Balance",
              summary
                ? `$${Math.round(summary.open_balance).toLocaleString()}`
                : "—",
              "text-amber-300",
              "/invoices",
              "bg-amber-400",
            ],
          ].map(([label, value, color, to, bar]) => (
            <Link
              key={label}
              to={to}
              data-testid={`dash-stat-${label.toLowerCase().replace(/[ ²]/g, "-")}`}
              className="group flex overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-all duration-150 hover:-translate-y-0.5 hover:border-zinc-700"
            >
              <span className={`w-1 shrink-0 ${bar}`} />
              <span className="flex-1 p-4">
                <span className="block text-xs uppercase tracking-widest text-zinc-500">
                  {label}
                </span>
                <span
                  className={`font-mono-data mt-1 block text-2xl font-bold ${color}`}
                >
                  {value}
                </span>
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <ProjectBlockersFeed
            projects={attention.projects}
            conflicts={attention.conflicts}
            loading={attention.loading}
            compact
          />
          <AlertsWidget />
        </div>

        <div
          className="mt-6 rounded-lg border border-zinc-800 bg-zinc-800/40 p-4"
          data-testid="follow-up-inbox"
        >
          <div className="flex items-center gap-2">
            <BellRinging size={16} weight="bold" className="text-red-400" />
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Follow-ups Due
            </div>
            <span
              className="ml-auto h-2 w-2 rounded-full bg-red-400"
              data-testid="follow-up-dot"
              aria-hidden="true"
            />
            <span className="font-mono-data text-sm font-bold text-red-400">
              {followUps.length}
            </span>
          </div>
          {followUps.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500">
              Nothing due — all follow-ups are clear.
            </p>
          ) : (
            <div className="mt-3 grid gap-2 lg:grid-cols-2">
              {followUps.map((f) => (
                <div
                  key={`${f.entity}-${f.activity_id || f.id}`}
                  data-testid={`follow-up-${f.entity}-${f.activity_id || f.id}`}
                  className="flex items-center gap-3 rounded-md border border-red-500/25 bg-red-500/5 p-3"
                >
                  <Link
                    to={
                      f.entity === "job"
                        ? `/projects/${f.id}?tab=field`
                        : `/customers/${f.id}`
                    }
                    className="min-w-0 flex-1"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                        {f.entity === "job" ? "Job" : f.type}
                      </span>
                      <span className="truncate text-sm font-semibold">
                        {f.name}
                      </span>
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400"
                        aria-hidden="true"
                      />
                      <span className="font-mono-data text-xs text-red-300">
                        {f.date}
                      </span>
                    </div>
                    <div className="truncate text-xs text-zinc-400">
                      {f.title}
                    </div>
                  </Link>
                  {f.phone && (
                    <a
                      href={`tel:${f.phone}`}
                      data-testid={`follow-up-call-${f.activity_id || f.id}`}
                      className="shrink-0 text-zinc-400 hover:text-yellow-400"
                      title={f.phone}
                    >
                      <Phone size={16} weight="bold" />
                    </a>
                  )}
                  <button
                    data-testid={`follow-up-done-${f.activity_id || f.id}`}
                    onClick={() => clearFollowUp(f)}
                    title="Mark followed up"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 transition-colors duration-150 hover:border-emerald-500 hover:text-emerald-400"
                  >
                    <Check size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <UpcomingViewings />
        <DesignerSends />

        <div
          className="mt-8 grid gap-4 xl:grid-cols-7"
          data-testid="week-board"
        >
          {days.map((d, i) => {
            const key = iso(d);
            const list = weekEvents
              .filter((e) => e.date === key)
              .sort((a, b) => a.kind.localeCompare(b.kind));
            return (
              <div
                key={key}
                data-testid={`week-day-${key}`}
                className={`rounded-lg border p-3 transition-colors duration-150 ${
                  key === today
                    ? "border-indigo-400/50 bg-indigo-500/5"
                    : "border-zinc-800 bg-zinc-800/30"
                }`}
              >
                <div className="flex items-center justify-between border-b border-zinc-800 pb-2.5">
                  <span
                    className={`text-xs font-semibold uppercase tracking-widest ${
                      key === today ? "text-indigo-200" : "text-zinc-400"
                    }`}
                  >
                    {DAY_NAMES[i]}
                  </span>
                  <span className="font-mono-data text-xs text-zinc-500">
                    {key.slice(5)}
                  </span>
                </div>
                <div className="mt-2.5 space-y-2">
                  {list.length === 0 && (
                    <p className="rounded-md border border-dashed border-zinc-800 p-3 text-center text-xs text-zinc-600">
                      Clear
                    </p>
                  )}
                  {list.map((e, n) => (
                    <Link
                      key={n}
                      to={
                        e.entity === "service"
                          ? `/projects/service/${e.job_id}/edit`
                          : `/projects/${e.job_id}?tab=field`
                      }
                      data-testid={`week-event-${e.job_id}-${e.kind}`}
                      className="group flex overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 transition-all duration-150 hover:-translate-y-px hover:border-zinc-700"
                    >
                      <span
                        className={`w-1 shrink-0 ${
                          {
                            Template: "bg-purple-400",
                            Fabrication: "bg-yellow-400",
                            Install: "bg-blue-400",
                            Service: "bg-red-400",
                          }[e.kind] || "bg-zinc-600"
                        }`}
                      />
                      <span className="min-w-0 flex-1 p-2.5">
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-mono-data text-[10px] text-yellow-500">
                            {e.code}
                          </span>
                          <span
                            className={`rounded-full border px-1.5 py-px text-[9px] uppercase tracking-widest ${
                              KIND_STYLE[e.kind]
                            }`}
                          >
                            {e.kind}
                          </span>
                        </span>
                        <span className="mt-1 block truncate text-sm font-semibold">
                          {e.project_name}
                        </span>
                        <span className="block truncate text-xs text-zinc-500">
                          {e.customer_name || "—"}
                        </span>
                        <span className="mt-1.5 flex items-center gap-1 text-[11px] text-zinc-500">
                          <User size={11} weight="bold" />{" "}
                          {e.crew || "Unassigned"} · {e.window}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Process Pipeline
              </div>
              <Link
                to="/projects"
                className="text-xs text-yellow-400 hover:underline"
                data-testid="pipeline-link"
              >
                Stage board
              </Link>
            </div>
            <div className="mt-4 space-y-3" data-testid="pipeline-list">
              {JOB_STAGES.map((stage) => {
                const count = jobs.filter((j) => j.stage === stage).length;
                const pct = jobs.length ? (count / jobs.length) * 100 : 0;
                return (
                  <div key={stage} data-testid={`pipeline-${stage}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: STAGE_HEX[stage] || "#a1a1aa" }}
                      />
                      <span className="text-zinc-300">{stage}</span>
                      <span className="font-mono-data ml-auto text-zinc-400">
                        {count}
                      </span>
                    </div>
                    <div
                      className="mt-1 h-1.5 w-full overflow-hidden rounded-full"
                      style={{ backgroundColor: `${STAGE_HEX[stage] || "#a1a1aa"}26` }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: STAGE_HEX[stage] || "#a1a1aa",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-4">
            <div className="flex items-center gap-2">
              <Warning size={16} weight="bold" className="text-red-400" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Stalled Jobs
              </div>
            </div>
            <div className="mt-4 space-y-2" data-testid="stalled-list">
              {stalled.length === 0 && (
                <p className="text-sm text-zinc-500">
                  Nothing is stalling — nice.
                </p>
              )}
              {stalled.slice(0, 6).map((j) => (
                <Link
                  key={j.id}
                  to={`/projects/${j.id}?tab=field`}
                  data-testid={`stalled-${j.id}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-red-500/30 bg-red-500/5 p-3 transition-colors duration-150 hover:border-red-400"
                >
                  <div className="min-w-0">
                    <div className="font-mono-data text-[10px] text-yellow-500">
                      {j.code}
                    </div>
                    <div className="truncate text-sm font-semibold">
                      {j.project_name}
                    </div>
                    <div className="text-xs text-zinc-500">{j.stage}</div>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span
                      className="h-2 w-2 rounded-full bg-red-400"
                      aria-hidden="true"
                    />
                    <span className="font-mono-data text-xs font-bold text-red-300">
                      {daysInStage(j)}d
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench size={16} weight="bold" className="text-orange-300" />
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Open Service
                </div>
              </div>
              <Link
                to="/projects/service"
                className="text-xs text-yellow-400 hover:underline"
                data-testid="service-link"
              >
                All calls
              </Link>
            </div>
            <div className="mt-4 space-y-2" data-testid="open-service-list">
              {openCalls.length === 0 && (
                <p className="text-sm text-zinc-500">No open service calls.</p>
              )}
              {openCalls.slice(0, 6).map((c) => (
                <Link
                  key={c.id}
                  to={`/projects/service/${c.id}/edit`}
                  data-testid={`open-service-${c.id}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3 transition-colors duration-150 hover:border-zinc-700"
                >
                  <div className="min-w-0">
                    <div className="font-mono-data text-[10px] text-yellow-500">
                      {c.code}
                    </div>
                    <div className="truncate text-sm font-semibold">
                      {c.issue_type}
                    </div>
                    <div className="truncate text-xs text-zinc-500">
                      {c.customer_name || "—"}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-zinc-400">
                    {c.scheduled_date || "unscheduled"}
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/inventory/reporting"
              data-testid="dash-reporting-link"
              className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-yellow-400"
            >
              <Stack size={14} weight="bold" /> Stock reporting
            </Link>
          </div>
        </div>

        <div className="mt-6" data-testid="overdue-pos-row">
          <OverduePOs />
        </div>
      </main>
    </div>
  );
}
